import { inject, Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import {
  AnhangDtoModel,
  AntragDtoModel,
  AuthenticationFacade,
  ConfigSettingsService,
  ConverterService,
  EndToEndEncrypter,
  Guid,
  MimeTypeMapReverse,
  ResourceProvider,
  TokenState,
  UploadDataForAntragsSeite,
} from '@ebeihilfe/core';
import { AnhangDataType, AntragMetaData, FormResult, FormValue } from '@ebeihilfe/einreichung-shared/model';
import { EinreichungApiService as SharedEinreichungApiService } from '@ebeihilfe/einreichung-shared/services';
import { LoadingController } from '@ionic/angular/standalone';
import { firstValueFrom, of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Antrag, Beleg, BelegType, Seite, SeiteFactory } from '../types';
import { AntragDtoAssemblerService } from './antrag-dto-assembler.service';
import { EinreichungApiService } from './einreichung-api.service';

@Injectable()
export class EinreichungService {
  private readonly antragDtoAssembler = inject(AntragDtoAssemblerService);
  private readonly loadingController = inject(LoadingController);
  private readonly sharedEinreichungApi = inject(SharedEinreichungApiService);

  public readonly jumpToUpload$ = new Subject<void>();

  protected readonly auth = inject(AuthenticationFacade);
  protected readonly configService = inject(ConfigSettingsService);
  protected readonly converterService = inject(ConverterService);
  protected readonly einreichungsApi = inject(EinreichungApiService);
  protected readonly endToEndEncrypter = inject(EndToEndEncrypter);
  protected readonly resource = inject(ResourceProvider);
  protected readonly route = inject(ActivatedRoute);
  protected readonly seiteFactory = inject(SeiteFactory);

  public async *transformToUploadData(
    antrag: Antrag,
  ): AsyncGenerator<[Seite, BelegType, (UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null]> {
    for (const beleg of antrag.belege.filter((b) => b.seiten.length > 0)) {
      for await (const uploadDataForSingleSeite of this.transformBeleg(antrag.antragsId, antrag.timestamp, beleg)) {
        yield uploadDataForSingleSeite;
      }
    }
  }

  public async checkForInteruptedAntrag(): Promise<string | null> {
    const isAuthenticated = await this.auth.isAuthenticated();
    if (!isAuthenticated) {
      return null;
    }

    const { customizing } = this.configService.appSettings;
    if (!customizing.hasUnfinishedAntrage) {
      return null;
    }

    return await this.hasUnfinishedAntragOnServer();
  }

  public async populateAntragFromAntragsInfo(antragId?: string): Promise<Antrag> {
    const dto = await this.getInteruptedAntragFromServer(antragId);
    const firstBelegMandatoryConfig = this.configService.appSettings.customizing.firstBelegMandatoryConfig;

    if (dto.tokenState === TokenState.Outdated) {
      throw new Error(_('new_antrag.outdatedEncryption'));
    }

    const antrag = this.antragDtoAssembler.fromDto(dto, firstBelegMandatoryConfig);

    if (dto.metadata) {
      antrag.setAntragsData(await this.decryptAndParseMetadata(dto.metadata.toString()));
    }

    if (dto.surveyDaten) {
      antrag.setSurveyData(await this.decryptSurveyData(dto.surveyDaten.toString()));
    }

    for (const belegSeiten of this.groupSeitenByBeleg(dto.antragsseiteInfo).values()) {
      await this.populateBelegFromAntragsInfo(
        antrag,
        belegSeiten.sort((a, b) => a.seitenNr - b.seitenNr),
      );
    }

    return antrag;
  }

  public async populateSeiteFromAntragsInfo(seitenInfo: AnhangDtoModel): Promise<Seite> {
    return this.seiteFactory.CreateFromSeitenInfo(seitenInfo);
  }

  /**
   * Führt den Upload der Belegseiten durch, zeigt währenddessen einen Ladeindikator an.
   *
   * @param antrag - Der Antrag, dessen Belege hochgeladen werden sollen.
   */
  public async uploadPagesWithLoading(antrag: Antrag): Promise<void> {
    const loading = await this.loadingController.create({});
    await loading.present();
    try {
      await this.uploadPages(antrag);
    } finally {
      await loading.dismiss();
    }
  }

  /**
   * Führt den Upload der Belegseiten durch.
   * Verwendet einen Generator, um Uploads schrittweise zu verarbeiten.
   * Fehler bei einzelnen Seiten stoppen nicht den gesamten Prozess.
   *
   * @param antrag - Der Antrag mit den hochzuladenden Belegen.
   */
  private async uploadPages(antrag: Antrag): Promise<void> {
    if (!antrag) {
      return;
    }

    const partialAntrag = {
      antragsId: antrag.antragsId.value,
      timestamp: antrag.timestamp,
      belege: antrag.belege,
    } as unknown as Antrag;

    try {
      const uploadGenerator = this.transformToUploadData(partialAntrag);

      for await (const [seite, belegType, uploadSeite] of uploadGenerator) {
        try {
          if (uploadSeite) {
            // seite has not yet been uploaded
            await firstValueFrom(this.sharedEinreichungApi.uploadAnhang(uploadSeite));
            seite.uploaded = true;
          } else if (seite.id) {
            // seite exists on the server and belegType is updated.
            await firstValueFrom(this.sharedEinreichungApi.updateBelegType(antrag.antragsId, seite.id, belegType));
          }
        } catch (error: unknown) {
          console.error('Error processing seite:', error);
        }
      }
    } catch (error: unknown) {
      console.error('Error during page upload process:', error);
      throw error;
    }
  }

  private getInteruptedAntragFromServer(antragId = ''): Promise<AntragDtoModel> {
    // for now, we only use the first unfinished

    return firstValueFrom(
      (antragId ? this.resource.getApplicationById(antragId) : this.einreichungsApi.getUnfinishedAntraege()).pipe(
        catchError((error) => {
          if (error?.message === 'server.error.missingData') {
            return of([]);
          }
          throw of(error);
        }),
        map((antrags) => {
          if (antragId && antrags) {
            return antrags;
          }

          return antrags?.[0];
        }),
      ),
    );
  }

  private async hasUnfinishedAntragOnServer(): Promise<string | null> {
    const antrage: AntragDtoModel = await this.getInteruptedAntragFromServer();
    return antrage != null ? antrage.id : null;
  }

  private async *transformBeleg(
    antragsId: Guid,
    antragsCreatedOn: string,
    beleg: Beleg,
  ): AsyncGenerator<[Seite, BelegType, (UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null]> {
    for (const seite of beleg.seiten) {
      const result = await this.transformSinglePage(
        seite,
        seite.seitenNumber,
        beleg.belegNumber,
        antragsId,
        antragsCreatedOn,
        beleg.belegType,
      );
      yield [seite, beleg.belegType, result];
    }
  }

  private async transformSinglePage(
    seite: Seite,
    seitenNumber: number,
    belegNumber: number,
    antragsId: Guid,
    antragsTimestamp: string,
    belegType: BelegType,
  ): Promise<(UploadDataForAntragsSeite & { dataType?: AnhangDataType }) | null> {
    // Skip if already uploaded
    if (seite.uploaded) {
      return null;
    }

    const file = await seite.getFile();
    const pageData = await this.endToEndEncrypter.encryptBlob(file);
    const dataType = MimeTypeMapReverse.get(file.type) as AnhangDataType;

    return {
      antragId: antragsId.toString(),
      pageData,
      belegNumber: belegNumber.toString(10),
      seiteNumber: seitenNumber.toString(10),
      thumb: seite.thumb,
      timestamp: antragsTimestamp,
      belegtyp: belegType.toString(10),
      dataType,
      pageCount: seite.pageCount,
    };
  }

  private async decryptSurveyData(encryptedSurveyData: string): Promise<Record<string, unknown>> {
    const decryptedData = await this.endToEndEncrypter.decryptFromBase64(encryptedSurveyData);
    const convertedToString = this.converterService.uIntArrayToString(decryptedData);
    return JSON.parse(convertedToString);
  }

  private async decryptAndParseMetadata(encryptedMetadata: string): Promise<AntragMetaData[]> {
    const decryptedData = await this.endToEndEncrypter.decryptFromBase64(encryptedMetadata);
    const convertedToString = this.converterService.uIntArrayToString(decryptedData);
    const parsedMetadata = JSON.parse(convertedToString);

    if (this.configService.appSettings.einreichungV3Activated) {
      return parsedMetadata.formResults.map(
        (formResult: FormResult) => new AntragMetaData({ formResults: [formResult] }),
      );
    }

    return (
      parsedMetadata.formResults
        .find((form: FormResult) => form.templateName === '::LEGACY::')
        ?.results.map((formValue: FormValue) =>
          FormValue.createLegacy(formValue.name, formValue.$type, formValue.value),
        ) ?? []
    );
  }

  private groupSeitenByBeleg(seiten: AnhangDtoModel[]): Map<number, AnhangDtoModel[]> {
    const belege = new Map<number, AnhangDtoModel[]>();
    seiten
      .filter((seite) => seite.dataType !== 'form')
      .sort((a, b) => a.belegNr - b.belegNr)
      .forEach((seite) => {
        const group = belege.get(seite.belegNr);
        if (!group) {
          belege.set(seite.belegNr, [seite]);
        } else {
          group.push(seite);
        }
      });
    return belege;
  }

  private async populateBelegFromAntragsInfo(antrag: Antrag, seitenToAddToBeleg: AnhangDtoModel[]): Promise<void> {
    const belegNr = seitenToAddToBeleg[0]?.belegNr;
    const beleg = antrag.addBeleg(belegNr);
    beleg.belegType = seitenToAddToBeleg[0]?.belegtyp as BelegType;

    for (const seitenInfo of seitenToAddToBeleg.sort((a, b) => a.seitenNr - b.seitenNr)) {
      const seite = await this.populateSeiteFromAntragsInfo(seitenInfo);
      beleg.addSeite(seite, seitenInfo.seitenNr);
    }
  }
}
